import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { productSubmissionsAPI } from '../../services/api';

export default function ProductSubmissionDialog({ open, onClose, onSubmitted }) {
  const [barcode, setBarcode] = useState('');
  const [productName, setProductName] = useState('');
  const [caloriesPer100g, setCaloriesPer100g] = useState('');
  const [proteinPer100g, setProteinPer100g] = useState('');
  const [carbsPer100g, setCarbsPer100g] = useState('');
  const [fatPer100g, setFatPer100g] = useState('');
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setBarcode('');
    setProductName('');
    setCaloriesPer100g('');
    setProteinPer100g('');
    setCarbsPer100g('');
    setFatPer100g('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const handleLookup = async () => {
    if (!barcode.trim()) return;
    setLookupLoading(true);
    setError('');
    try {
      const res = await productSubmissionsAPI.lookup(barcode.trim());
      const data = res.data?.data;
      if (data?.productName) setProductName(data.productName);
      if (data?.caloriesPer100g != null) setCaloriesPer100g(String(data.caloriesPer100g));
      if (data?.proteinPer100g != null) setProteinPer100g(String(data.proteinPer100g));
      if (data?.carbohydratesPer100g != null) setCarbsPer100g(String(data.carbohydratesPer100g));
      if (data?.fatPer100g != null) setFatPer100g(String(data.fatPer100g));
    } catch (err) {
      setError(err.response?.data?.message || 'Barcode not found — enter details manually.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!barcode.trim() || !productName.trim()) {
      setError('Barcode and product name are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('barcode', barcode.trim());
      formData.append('productName', productName.trim());
      formData.append('caloriesPer100g', caloriesPer100g || '0');
      formData.append('proteinPer100g', proteinPer100g || '0');
      formData.append('carbsPer100g', carbsPer100g || '0');
      formData.append('fatPer100g', fatPer100g || '0');
      await productSubmissionsAPI.create(formData);
      onSubmitted?.();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Submit a food product</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              label="Barcode"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
            />
            <Button variant="outlined" onClick={handleLookup} disabled={lookupLoading || !barcode.trim()} sx={{ flexShrink: 0, mt: 0.5 }}>
              {lookupLoading ? '…' : 'Lookup'}
            </Button>
          </Stack>
          <TextField fullWidth label="Product name" value={productName} onChange={(e) => setProductName(e.target.value)} required />
          <TextField fullWidth label="Calories per 100g" type="number" value={caloriesPer100g} onChange={(e) => setCaloriesPer100g(e.target.value)} />
          <TextField fullWidth label="Protein per 100g (g)" type="number" value={proteinPer100g} onChange={(e) => setProteinPer100g(e.target.value)} />
          <TextField fullWidth label="Carbs per 100g (g)" type="number" value={carbsPer100g} onChange={(e) => setCarbsPer100g(e.target.value)} />
          <TextField fullWidth label="Fat per 100g (g)" type="number" value={fatPer100g} onChange={(e) => setFatPer100g(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Submitting…' : 'Submit for review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
